/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import React, { Component, ReactNode, ReactElement, ErrorInfo } from 'react'
import { ErrorDisplay } from './ErrorDisplay'
import { addReactError } from '@datadog/browser-rum-react'

interface ErrorBoundaryProps {
  /**
   * The component tree that will be guarded for error.
   */
  children: ReactNode
  /**
   * A ReactNode to be rendered when an error happens is caught.
   *
   * If a ReactElement is passed it will receive two additional props so we can
   * provide better UX: the error and a callback to be invoked if the user
   * wants to "retry" to fix the issue.
   */
  error?:
    | ReactNode
    | ReactElement<{
        /*
         * The error that caused the ErrorBoundary to be triggered.
         */
        error?: unknown
        /**
         * A function to be on an **user interaction** indicating we want
         * to retry mounting the component tree in the hopes of that
         * fixing the issue.
         */
        onRecoverAttempt?: () => void
      }>
  /**
   * Maximum number of times the children component tree will be allowed to
   * "retry mounting". Once the amount of attempts is exhausted `onRecoverAttemptsExhausted`
   * will be invoked instead
   *
   * Defaults to 1.
   */
  maxRecoverAttempts?: number
  /**
   * What to do once we deem the children component-tree non-recoverable.
   *
   * Defaults to performing a hard reload via `window.location.reload()`.
   */
  onRecoverAttemptsExhausted?: () => void
}

interface ErrorBoundaryState {
  recoverAttempts: number
  hasError: boolean
  error: unknown
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private readonly maxRecoverAttempts: number

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.maxRecoverAttempts = props.maxRecoverAttempts ?? 1
    this.state = { recoverAttempts: 0, hasError: false, error: null }
  }

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    addReactError(error, errorInfo)
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRecoverAttempt = () => {
    if (this.state.recoverAttempts < this.maxRecoverAttempts) {
      this.setState((state) => ({
        hasError: false,
        error: null,
        recoverAttempts: state.recoverAttempts + 1,
      }))
    } else {
      this.handleRecoverAttemptsExhausted()
    }
  }

  handleRecoverAttemptsExhausted = () => {
    if (this.props.onRecoverAttemptsExhausted) {
      this.props.onRecoverAttemptsExhausted()
    } else {
      window.location.reload()
    }
  }

  renderErrorDisplay() {
    const errorDisplay: ReactNode = this.props.error ?? <ErrorDisplay />

    if (React.isValidElement<any>(errorDisplay)) {
      return React.cloneElement(errorDisplay, {
        onRecoverAttempt: () => this.handleRecoverAttempt(),
        ...(errorDisplay as any).props,
        error: this.state.error,
      })
    }

    return errorDisplay
  }

  render() {
    return this.state.hasError ? this.renderErrorDisplay() : this.props.children
  }
}
